import {
  type CatalogMatchDetail,
  type CatalogPlayer,
  type CatalogRosterRow,
  flagCode,
  type ProjectState,
  weatherConditionId,
} from "@livcg/schema";
import { gridHasData, gridSideOf } from "./lineupAir.js";

/** Goal record — same shape as BigEventItem in the graphic
 *  (graphics-src/score/src/modes/big.ts). */
export type OverlayEventLite = {
  firstName: string;
  lastName: string;
  minute: number;
  second?: number;
  added?: number;
  type: "goal" | "own_goal" | "penalty_goal";
};

/** Fields that are NOT in the replicant — they come from /api/catalog/matches/:id
 *  (+ /standings) and reach the graphic via the overlay.show data passthrough. */
export type OverlayExtras = {
  round?: string | null;
  kickoffAt?: string | null;
  matchStadium?: string | null;
  events?: { home: OverlayEventLite[]; away: OverlayEventLite[] };
  /** Per-match squad (for the roster/reserve overlays). */
  roster?: { home: CatalogRosterRow[]; away: CatalogRosterRow[] };
  /** Custom BIG free text (panels-local, not in the replicant). */
  customText?: string;
  /** The match's internal tournament id (t_<pflId>) — for standings fetch/sync. */
  tournamentId?: string | null;
  /** Tournament table rows (catalog /standings). */
  standings?: StandingsCatalogRow[];
};

/** A catalog /standings row (Task 1). teamId is the internal `c_<pflId>`. */
export type StandingsCatalogRow = {
  position: number;
  teamId: string;
  teamName: string;
  teamLogoUrl: string | null;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalDifference: number;
  points: number;
};

/** /api/catalog/matches/:id response shape — now from the shared contract (schema). */
export type MatchDetailLite = CatalogMatchDetail;

/** Overlay extras from the catalog response: round/kickoff/stadium + goal list
 *  (goal / own_goal / penalty_goal; player names from players). */
export function buildOverlayExtras(detail: MatchDetailLite | null): OverlayExtras {
  if (!detail) return {};
  const players = new Map<string, CatalogPlayer>();
  for (const p of [...detail.home.players, ...detail.away.players]) players.set(p.id, p);
  const events: { home: OverlayEventLite[]; away: OverlayEventLite[] } = {
    home: [],
    away: [],
  };
  for (const ev of detail.events) {
    if (ev.type !== "goal" && ev.type !== "own_goal" && ev.type !== "penalty_goal") continue;
    const side = ev.teamId === detail.home.team.id ? "home" : "away";
    const player = players.get(ev.playerId);
    events[side].push({
      firstName: player?.firstName ?? "",
      lastName: player?.lastName ?? "",
      minute: ev.minute,
      ...(ev.data?.added ? { added: ev.data.added } : {}),
      ...(ev.second > 0 && { second: ev.second }),
      type: ev.type,
    });
  }
  return {
    round: detail.match.round,
    kickoffAt: detail.match.kickoffAt,
    matchStadium: detail.match.stadium,
    tournamentId: detail.match.tournamentId,
    events,
    roster: { home: detail.home.roster ?? [], away: detail.away.roster ?? [] },
  };
}

/** MySQL datetime ("YYYY-MM-DD HH:MM:SS") → "DD.MM.YYYY". */
function formatMatchDate(kickoffAt: string | null | undefined): string {
  if (!kickoffAt) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(kickoffAt);
  if (!m) return "";
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** Build the `overlay.show` data payload for crew / sponsor / coach modes from
 *  the project's stored Setup info. Shapes match what the score overlay
 *  expects (titre-lower, names panel, swap — see graphics-src/score). Modes
 *  not listed here carry an empty payload. */
function fullName(p: { firstName: string; lastName: string } | null | undefined): string {
  return p ? [p.firstName, p.lastName].filter(Boolean).join(" ").trim() : "";
}

type RosterPayloadPlayer = {
  firstName: string;
  lastName: string;
  number: number | null;
  isGk: boolean;
  /** ISO code in lowercase (e.g. "br") — foreigners only, null for UZ. */
  flag: string | null;
  isCaptain: boolean;
};

/** roster_* / reserve_* payload: from the catalog squad (filtered by
 *  isStarter), or if absent from the lineup replicant slots (main only).
 *  Empty list → {} (the graphic's buildRosterPayload returns null, it doesn't open). */
function buildRosterData(
  mode: string,
  state: ProjectState,
  extras: OverlayExtras,
): Record<string, unknown> {
  const side: "home" | "away" = mode.endsWith("away") ? "away" : "home";
  const type: "main" | "reserve" = mode.startsWith("reserve") ? "reserve" : "main";
  const team = side === "home" ? state.teams.home : state.teams.away;

  const squad = extras.roster?.[side] ?? [];
  // Captain — the operator marks it in GridPanel (PFL has no captain data).
  const captainId = state.grid[side].find((g) => g.captain)?.playerId ?? null;
  let players: RosterPayloadPlayer[] = squad
    .filter((p) => p.isStarter === (type === "main"))
    .map((p) => ({
      firstName: p.firstName,
      lastName: p.lastName,
      number: p.number,
      isGk: p.position === "GK",
      flag: flagCode(p.citizenship),
      isCaptain: captainId !== null && p.playerId === captainId,
    }));

  // Fallback: if the squad is empty, build the starting lineup from the lineup slots
  // (main only; no reserve fallback — it stays empty, the tile is a no-op).
  if (players.length === 0 && type === "main") {
    players = state.lineup.slots
      .filter((slot) => slot.playerId !== null || slot.firstName || slot.lastName)
      .map((slot) => ({
        firstName: slot.firstName,
        lastName: slot.lastName,
        number: slot.number,
        isGk: false,
        flag: null,
        isCaptain: captainId !== null && slot.playerId === captainId,
      }));
  }

  if (players.length === 0) return {};
  return {
    side,
    type,
    teamName: team?.name ?? "",
    teamLogoUrl: team?.logoUrl ?? null,
    coachPhotoUrl: state.crew?.[side === "home" ? "coachHome" : "coachAway"]?.photoUrl ?? null,
    players,
  };
}

/** The set of roster_* / reserve_* modes — for the data-signal. */
const ROSTER_MODES = new Set<string>([
  "roster_home",
  "roster_away",
  "reserve_home",
  "reserve_away",
]);

/** The set of tournament_* modes — for the data-signal. */
const TOURNAMENT_MODES = new Set<string>(["tournament_1_8", "tournament_9_16"]);

/** player_compare_* — payload comes from the extension (state.playerCompare), the panel doesn't compute it. */
const PLAYER_COMPARE_MODES = new Set<string>(["player_compare_pre", "player_compare_ht"]);

export function playerCompareSceneOf(mode: string): "pre" | "ht" {
  return mode.endsWith("_ht") ? "ht" : "pre";
}

/** Tile guard (pc3-guidance): ready + the payload scene matches the tile + there are rows.
 *  Otherwise the graphic doesn't come up in enterItem, but the scoreboard would already
 *  have been hidden — a black screen. */
export function playerCompareHasData(mode: string, state: ProjectState): boolean {
  const pc = state.playerCompare;
  if (!pc || pc.status !== "ready" || !pc.payload) return false;
  return pc.payload.scene === playerCompareSceneOf(mode) && pc.payload.rows.length > 0;
}

/** `player_stat` titre: the extension's computation is ready and there is at least one stat. */
export function playerStatHasData(state: ProjectState): boolean {
  const ps = state.playerStat;
  return !!ps && ps.status === "ready" && !!ps.payload && ps.payload.items.length > 0;
}

/** Disabled tile tooltip text — different for the tournament table and the roster. */
export function noDataTitle(mode: string): string {
  if (mode === "player_stat") {
    return "O'yinchi tanlanmagan \u2014 Boshqa \u2192 O'yinchilar \u2192 Bitta o'yinchi";
  }
  if (TOURNAMENT_MODES.has(mode)) return "Jadval sinxronlanmagan \u2014 overlay mavjud emas";
  if (PLAYER_COMPARE_MODES.has(mode)) {
    return (
      "O'yinchilar solishtiruvi tayyor emas \u2014 Boshqa \u2192 " +
      "O'yinchilar drawer'ida tanlang (sahna mos bo'lsin)"
    );
  }
  if (gridSideOf(mode)) return "Doska ham, Lineup slotlari ham bo'sh \u2014 Grid chiqmaydi";
  return "Tarkib ma'lumoti yo'q \u2014 overlay mavjud emas";
}

/** tournament_1_8 / tournament_9_16 payload: the full 16-row table +
 *  the current teams' ids (the graphic does the slice + highlight). Empty standings →
 *  {} (the graphic's buildStandingsPayload returns null, it doesn't open). */
function buildStandingsData(state: ProjectState, extras: OverlayExtras): Record<string, unknown> {
  const rows = extras.standings ?? [];
  if (rows.length === 0) return {};
  return {
    rows,
    round: extras.round ?? "",
    homeTeamId: state.teams.home?.id ?? null,
    awayTeamId: state.teams.away?.id ?? null,
  };
}

/** Is there data worth sending overlay.show for this mode?
 *  Applies only to roster_* / reserve_* modes — `false` for an empty squad/reserve
 *  (buildRosterData returns `{}`), otherwise `true`.
 *  Always `true` for non-roster modes (so the caller can call it
 *  unconditionally). Spec: empty reserve/roster → no overlay.show,
 *  tile disabled (docs/.../big-overlays-phase2-design.md "Ma'lumot oqimi"). */
export function rosterModeHasData(
  mode: string,
  state: ProjectState,
  extras: OverlayExtras,
): boolean {
  if (TOURNAMENT_MODES.has(mode)) return (extras.standings?.length ?? 0) > 0;
  const gridSide = gridSideOf(mode);
  if (gridSide) return gridHasData(state, gridSide);
  if (PLAYER_COMPARE_MODES.has(mode)) return playerCompareHasData(mode, state);
  if (mode === "player_stat") return playerStatHasData(state);
  if (!ROSTER_MODES.has(mode)) return true;
  return Object.keys(buildRosterData(mode, state, extras)).length > 0;
}

export function overlayDataFor(
  mode: string,
  state: ProjectState,
  extras: OverlayExtras = {},
): Record<string, unknown> {
  const c = state.crew;
  const m = state.metadata;
  const t = state.teams;

  switch (mode) {
    case "little_names_commentator": {
      const name1 = fullName(c?.commentator1);
      const name2 = fullName(c?.commentator2);
      return { ...(name1 && { name1 }), ...(name2 && { name2 }) };
    }
    case "little_names_ref":
      return {
        referee: fullName(c?.referee),
        helper1: fullName(c?.refereeHelper1),
        helper2: fullName(c?.refereeHelper2),
        helper3: fullName(c?.refereeHelper3),
      };
    case "little_names_ref_helper":
      return {
        helper1: fullName(c?.refereeHelper1),
        helper2: fullName(c?.refereeHelper2),
        helper3: fullName(c?.refereeHelper3),
      };
    case "little_names_var":
      return { varName: fullName(c?.var), avarName: fullName(c?.avar) };
    case "little_names_universal":
      return m?.universal
        ? {
            firstName: m.universal.firstName,
            lastName: m.universal.lastName,
            label: m.universal.label,
          }
        : {};
    case "coach_id_home":
      return c?.coachHome
        ? {
            side: "home",
            firstName: c.coachHome.firstName,
            lastName: c.coachHome.lastName,
            logoUrl: t.home?.logoUrl ?? null,
          }
        : {};
    case "coach_id_away":
      return c?.coachAway
        ? {
            side: "away",
            firstName: c.coachAway.firstName,
            lastName: c.coachAway.lastName,
            logoUrl: t.away?.logoUrl ?? null,
          }
        : {};
    case "little_sponsor":
      return m?.sponsor1
        ? {
            name: m.sponsor1.name,
            tagline: m.sponsor1.tagline,
            logoUrl: m.sponsor1.logoUrl ?? null,
          }
        : {};
    case "little_sponsor_2":
      return m?.sponsor2
        ? {
            name: m.sponsor2.name,
            tagline: m.sponsor2.tagline,
            logoUrl: m.sponsor2.logoUrl ?? null,
          }
        : {};
    case "big":
      // Score/names/colors/logos are taken from the replicant in the graphic;
      // the goal list, however, exists only in the DB — it goes from here.
      return { ...(extras.events && { events: extras.events }) };
    case "stadium":
      // The value in Setup wins, otherwise the match stadium from the catalog —
      // the same chain as MATCHID. Previously there was no fallback here and
      // if the operator hadn't filled in Setup the tableau came up with an
      // empty title (even though the stadium was in the DB).
      return {
        stadium: m?.stadium || extras.matchStadium || "",
        city: m?.city ?? "",
      };
    case "matchid":
      return {
        round: extras.round ?? "",
        date: formatMatchDate(extras.kickoffAt),
        stadium: m?.stadium || extras.matchStadium || "",
        city: m?.city ?? "",
        homeLogoUrl: t.home?.logoUrl ?? null,
        awayLogoUrl: t.away?.logoUrl ?? null,
      };
    case "roster_home":
    case "roster_away":
    case "reserve_home":
    case "reserve_away":
      return buildRosterData(mode, state, extras);
    case "stats":
      // Score/names/numbers/logos are derived from the replicant in the graphic.
      return {};
    case "weather":
      return {
        stadium: m?.stadium || extras.matchStadium || "",
        temperature: state.weather?.temperature != null ? String(state.weather.temperature) : "",
        humidity: state.weather?.humidity != null ? String(state.weather.humidity) : "",
        wind: state.weather?.wind != null ? String(state.weather.wind) : "",
        condition: weatherConditionId(state.weather?.condition),
      };
    case "custom":
      return { text: extras.customText ?? "" };
    case "tournament_1_8":
    case "tournament_9_16":
      return buildStandingsData(state, extras);
    case "player_compare_pre":
    case "player_compare_ht":
      // The payload goes FLAT — the graphic parses it directly with
      // playerComparePayloadSchema.
      return playerCompareHasData(mode, state)
        ? ((state.playerCompare?.payload ?? {}) as Record<string, unknown>)
        : {};
    case "player_stat":
      return playerStatHasData(state)
        ? ((state.playerStat?.payload ?? {}) as Record<string, unknown>)
        : {};
    case "little_extra_time":
      return { extraMinutes: state.match.additionalTime };
    default:
      return {};
  }
}
